package com.schoolconnect.app.designsystem

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableLongStateOf
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.runtime.AppErrorCenter
import com.schoolconnect.app.runtime.AppErrorEvent
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.close_x
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.painterResource

private const val APP_ERROR_SNACKBAR_DURATION_MS = 4_000L

@Composable
fun AppErrorHost(
    modifier: Modifier = Modifier,
    maxWidth: androidx.compose.ui.unit.Dp = 560.dp,
    maxVisibleItems: Int = 3
) {
    val maxRetainedItems = maxOf(maxVisibleItems * 3, 10)
    val visibleItems = remember { mutableStateListOf<VisibleAppError>() }
    var nextId by remember { mutableLongStateOf(0L) }

    LaunchedEffect(Unit) {
        AppErrorCenter.events.collect { event ->
            // Bootstrap/session failures can be reported by more than one
            // layer during startup. Keep one visible message per text so the
            // error host does not cover the form with duplicate snackbars.
            if (visibleItems.none { it.event.message == event.message }) {
                val visible = VisibleAppError(id = nextId++, event = event)
                visibleItems.add(visible)
                while (visibleItems.size > maxRetainedItems) {
                    visibleItems.removeAt(0)
                }
            }
        }
    }

    if (visibleItems.isEmpty()) return

    Box(
        modifier = modifier.fillMaxSize(),
        contentAlignment = Alignment.TopCenter
    ) {
        Column(
            // Keep global alerts clear of the shell header and page title.
            modifier = Modifier.padding(start = 20.dp, end = 20.dp, top = 88.dp, bottom = 16.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            visibleItems.takeLast(maxVisibleItems).forEach { visible ->
                AppErrorSnackbarItem(
                    visible = visible,
                    onDismiss = { id -> visibleItems.removeAll { it.id == id } },
                    maxWidth = maxWidth
                )
            }
        }
    }
}

private data class VisibleAppError(
    val id: Long,
    val event: AppErrorEvent
)

@Composable
private fun AppErrorSnackbarItem(
    visible: VisibleAppError,
    onDismiss: (Long) -> Unit,
    maxWidth: androidx.compose.ui.unit.Dp
) {
    LaunchedEffect(visible.id) {
        delay(APP_ERROR_SNACKBAR_DURATION_MS)
        onDismiss(visible.id)
    }

    Surface(
        modifier = Modifier.widthIn(min = 280.dp, max = maxWidth),
        shape = RoundedCornerShape(8.dp),
        color = AppDangerSoft,
        border = BorderStroke(1.dp, AppDangerBorder),
        shadowElevation = 2.dp
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 12.dp),
            horizontalArrangement = Arrangement.spacedBy(10.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier.size(24.dp).background(AppDanger, CircleShape),
                contentAlignment = Alignment.Center
            ) {
                Text("!", color = androidx.compose.ui.graphics.Color.White, fontSize = 14.sp, fontWeight = FontWeight.Bold)
            }
            Text(
                text = visible.event.message,
                modifier = Modifier.weight(1f),
                color = AppNavy,
                fontSize = 14.sp,
                maxLines = 3,
                overflow = TextOverflow.Ellipsis
            )
            Box(
                modifier = Modifier.size(28.dp).clickable { onDismiss(visible.id) },
                contentAlignment = Alignment.Center
            ) {
                Icon(painter = painterResource(Res.drawable.close_x), contentDescription = "Dismiss error", tint = AppMuted, modifier = Modifier.size(16.dp))
            }
        }
    }
}
